import { useEffect, useState } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DateRangeFields from "./DateRangeFields";

const PRIMARY = "#6759FF";
const LINE_COLORS = ["#6759FF", "#F59E0B", "#10B981", "#EF4444", "#3B82F6"];
// Each selected metric gets its own y-axis (price, BSR and rating have nothing in common), so cap the count.
const MAX_METRICS = LINE_COLORS.length;

// Day-by-day lines of the selected metrics for one value of a dimension (an ASIN, a seller or a category).
// `baseFilterItems` are the page's filters (Product Type etc.); the dimension value is added on top.
// `metrics` are the marketplace's trend metrics (productTrackingConfigFor).
const TrendDialog = ({
  open, onClose, dimension, value, title, subtitle, imageUrl, metrics: allMetrics, dateRange, baseFilterItems, postQuery, formatValue,
}) => {
  const metrics = allMetrics.filter((m) => !(dimension === "productId" && m.hideForAsin));
  const [selected, setSelected] = useState([metrics[0].key]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Keep the menu order, not the click order, so colours stay put.
  const selectedMetrics = metrics.filter((m) => selected.includes(m.key));
  // The dialog's own range: starts as the page's range each time it opens, then changes here.
  const [range, setRange] = useState(dateRange);
  useEffect(() => {
    if (open) setRange(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || value === undefined || value === null || !selected.length) return;
    const controller = new AbortController();
    const query = {
      dimensions: ["date"],
      measures: selectedMetrics.map((m) => m.key),
      timeDimensions: [{ dimension: "date", dateRange: range }],
      filters: {
        dimensions: {
          type: "AND_RULE",
          and: [...baseFilterItems, { type: "RULE_ITEM", member: dimension, operator: "equals", values: [value] }],
        },
      },
      order: [["date", "asc"]],
    };
    setIsLoading(true);
    setError("");
    postQuery(query, controller.signal)
      .then((data) => {
        setRows(data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel?.(err)) return;
        setRows([]);
        setError(err?.response?.data?.message || "Could not load trend");
        setIsLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dimension, value, selected.join(","), range?.[0], range?.[1], baseFilterItems]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 6 }}>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt=""
            sx={{ width: 40, height: 52, objectFit: "contain", background: "#F6F6F7", borderRadius: "4px", flexShrink: 0 }}
          />
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={title}>
            {title || value}
          </Typography>
          {subtitle ? <Typography sx={{ fontSize: "12px", color: "#8C9196" }}>{subtitle}</Typography> : null}
        </Box>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2, mt: 1 }}>
          <DateRangeFields value={range} onChange={setRange} />
          <FormControl size="small" sx={{ minWidth: 240, maxWidth: 360 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Metrics</InputLabel>
            <Select
              multiple
              label="Metrics"
              value={selected}
              onChange={(e) => {
                const next = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                if (next.length && next.length <= MAX_METRICS) setSelected(next);
              }}
              renderValue={() => selectedMetrics.map((m) => m.label).join(", ")}
              sx={{ fontSize: "12px" }}
            >
              {metrics.map((m) => {
                const checked = selected.includes(m.key);
                return (
                  <MenuItem
                    key={m.key}
                    value={m.key}
                    disabled={!checked && selected.length >= MAX_METRICS}
                    sx={{ fontSize: "12px", py: 0 }}
                  >
                    <Checkbox size="small" checked={checked} sx={{ p: 0.5, mr: 1 }} />
                    <ListItemText primary={m.label} primaryTypographyProps={{ fontSize: "12px" }} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
        {error && (
          <Typography color="error" sx={{ fontSize: "13px", mb: 1 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ height: 340, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {isLoading ? (
            <CircularProgress size={28} sx={{ color: PRIMARY }} />
          ) : rows.length ? (
            <Line
              data={{
                labels: rows.map((r) => r.date),
                datasets: selectedMetrics.map((m, i) => ({
                  label: m.label,
                  data: rows.map((r) => (r[m.key] === undefined || r[m.key] === null ? null : Number(r[m.key]))),
                  borderColor: LINE_COLORS[i],
                  backgroundColor: LINE_COLORS[i],
                  yAxisID: `y_${m.key}`,
                  format: m.format,
                  tension: 0.3,
                  spanGaps: true,
                })),
              }}
              options={{
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                plugins: {
                  legend: { display: selectedMetrics.length > 1, position: "bottom" },
                  datalabels: { display: false },
                  tooltip: {
                    callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatValue(ctx.raw, ctx.dataset.format)}` },
                  },
                },
                scales: Object.fromEntries(
                  selectedMetrics.map((m, i) => [
                    `y_${m.key}`,
                    {
                      position: i % 2 === 0 ? "left" : "right",
                      beginAtZero: false,
                      grid: { drawOnChartArea: i === 0 },
                      ticks: { color: LINE_COLORS[i], callback: (v) => formatValue(v, m.format) },
                    },
                  ])
                ),
              }}
            />
          ) : (
            <Typography sx={{ color: "#8C9196", fontSize: "13px" }}>No scraped data for this date range.</Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TrendDialog;
